import React from 'react';

import { useTranslate } from '../../../../i18n/useTranslationModules';
import { getDefaultAgendaImage } from '../../../../store/utils/create-event';
import { LanguagesAbbr, PageModule, Session, SessionPreview } from '../../../../types/working-model';

import { getHomepageDefaultLanguage } from '../../../../utils/get-language';
import { useTypedSelector } from '../../../../store/reducers/use-typed-selector';
const EditorCalendarOndemandSessionCard = React.lazy(() => import('../../../admin/create/homepage/editor/agenda-editor/editor-calendar-ondemand-session-card'));
import CalendarOndemandSessionCard from './calendar-ondemand-session-card';
import useTranslationRoute from '../../hooks/use-translation-route';
import { AgendaCarousel } from './agenda-carousel';
import { OptionalComponent } from '../../../../utils/optional-component';
import { getOnDemandSessionsSelectedForDisplay, createDefaultTracksContent, getDisplayedTracksInSpecifiedOrder } from './agenda-utils';
import { SessionFilterArgs } from './session-filter';
import { isArray } from 'underscore';


interface IGroupedSessions {
	heading: string;
	sessions: Array<Session | SessionPreview>;
}

interface CalendarBroadcastLayoutProps {
	sessionGroup: IGroupedSessions;
	module: PageModule;
	language: LanguagesAbbr;
	editor?: boolean;
	template: string;
	isAgendaPage: boolean;
	filter: SessionFilterArgs;
}

export const CalendarOndemand = ({
	sessionGroup,
	module,
	language,
	editor,
	template,
	isAgendaPage,
	filter
}: CalendarBroadcastLayoutProps): JSX.Element => {

	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const baseLanguage: LanguagesAbbr | null = getHomepageDefaultLanguage(workingEvent);
	const translationRoute = useTranslationRoute();
	const { t } = useTranslate(['homepage', translationRoute, 'session']);

	const _ondemandTracksContent = module?.module_display_info?.session_display_options || createDefaultTracksContent(sessionGroup.sessions);
	const _ondemandTracksOrder = module?.module_display_info?.content_track_order || [];
	const _displayAllSettings = module.module_display_info?.session_display_all || { liveSessions: true, onDemandSessions: true };

	// Display all tracks for agenda, always
	const allTracks = getDisplayedTracksInSpecifiedOrder(sessionGroup.sessions, _ondemandTracksOrder);

	const ondemandTracksContent = getOnDemandSessionsSelectedForDisplay(
		_ondemandTracksContent,
		_displayAllSettings,
		sessionGroup.sessions,
		isAgendaPage,
	);

	const generateCarousel = (track: string, group: (Session | SessionPreview | undefined)[]) => {
		return (
			<AgendaCarousel track={track} group={group} isResponsive={editor === false}>
				{group.map((session, i) => (
					<OptionalComponent key={session?.uuid} display={!!session}>
						<React.Fragment>
							{editor ? (
								<React.Suspense fallback="">
									<EditorCalendarOndemandSessionCard
										baseLanguage={baseLanguage}
										language={language}
										session={session as Session}
										template={template}
										defaultImage={getDefaultAgendaImage(template, i)}
										isCalendarLayout={true}
									/>
								</React.Suspense>
							) : (
								<CalendarOndemandSessionCard
									session={session as Session}
									template={template}
									defaultImage={getDefaultAgendaImage(template, i)}
									isCalendarLayout={true}
									baseLanguage={baseLanguage}
									language={language}
								/>
							)}
						</React.Fragment>
					</OptionalComponent>
				))}
			</AgendaCarousel>
		);
	};

	const isTrackInFilter = (track: string) => {
		// In this case a filter isn't really set (page load) or no
		// tracks or Sessions are in filter so show all
		if (!filter
			|| !filter.sessionTypes
			|| !filter.sessionTypes.Sessions
			|| !isArray(filter.sessionTypes.Sessions)
			|| !filter.sessionTypes.Tracks
			|| !isArray(filter.sessionTypes.Tracks)
		) {
			return true;
		}

		// if on demand is selected but no tracks, that means show all of on-demand, it has no filters
		// turned on.  If even one track is selected, then we are filtering on that track or
		// each track that may also be selected.
		if (filter.sessionTypes.Sessions.includes('on-demand') && filter.sessionTypes.Tracks.length === 0) {
			return true;
		}

		return filter.sessionTypes.Tracks.includes(track);
	}

	return (
		<div className={'agenda-sessions-section carousel-main'}>
			{allTracks.map(track => {
				if (!isTrackInFilter(track)) return <></>
				if (!ondemandTracksContent[track] || ondemandTracksContent[track].filter(sess => sess).length === 0) {
					return <React.Fragment key={track}></React.Fragment>;
				}
				const trackName = track === 'bl_untracked' ? 'Uncategorized' : track;
				if (ondemandTracksContent[track]) {
					return ondemandTracksContent[track]?.length
						&& <React.Fragment key={`calendar-ondemand-${track}`}>{generateCarousel(t(`${translationRoute}:tracks.${trackName}`, trackName), ondemandTracksContent[track])}</React.Fragment>;
				}
			})}
		</div>
	);
};
